import { useEffect, useState } from 'react';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    document.title = '关于我们 - 7777影院 | 品牌介绍与联系方式';
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get('phone_alt') as string || '').trim();
    if (honeypotValue) {
      setFormStatus('success');
      form.reset();
      return;
    }
    formData.delete('phone_alt');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value as string);
      });
      const response = await fetch('https://readdy.ai/api/form/d9niusgbdkpk99j7gka0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        if (serverMsg.toLowerCase().includes('spam')) {
          setFormStatus('success');
          form.reset();
        } else {
          setFormStatus('error');
          setFormError(serverMsg || '提交失败，请稍后再试');
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  const organizationJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '7777影院',
    url: 'https://7777yingyuan.com',
    description: '7777影院汇集最新电影、免费电视剧和热门综艺节目，支持在线不缓冲播放，资源每日更新',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'contact@7777yingyuan.com',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '7777影院的影视资源需要付费吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '7777影院所有影视资源完全免费在线观看，无需注册会员，无需付费订阅。我们致力于为广大影迷提供最便捷的免费观影体验。',
        },
      },
      {
        '@type': 'Question',
        name: '7777影院的画质如何？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '7777影院支持1080P和4K超高清画质播放，采用先进的视频编码技术和CDN加速节点，确保您获得流畅、清晰的观影体验。',
        },
      },
      {
        '@type': 'Question',
        name: '7777影院的资源更新频率是怎样的？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '7777影院资源每日更新，最新上映的电影和热门电视剧会在第一时间同步上线，让您追剧不等待。',
        },
      },
      {
        '@type': 'Question',
        name: '如何在7777影院找到想看的影视内容？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过网站顶部的搜索框直接搜索影片名称，也可以浏览我们精心整理的分类频道，包括动作片、喜剧片、科幻片、电视剧、综艺等十二大分类，方便快捷地找到心仪的影视内容。',
        },
      },
      {
        '@type': 'Question',
        name: '7777影院支持哪些设备观看？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '7777影院全面支持电脑、平板和手机等各类设备在线观看，页面自适应不同屏幕尺寸，让您随时随地享受观影乐趣。',
        },
      },
    ],
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(organizationJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>

      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-8">
            关于7777影院
          </h1>

          <section className="mb-12">
            <div className="flex flex-col lg:flex-row gap-8 items-start">
              <div className="flex-1">
                <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4">品牌介绍</h2>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  7777影院是一家专注于在线影视播放的综合性视频平台，致力于为广大影迷提供最新电影、免费电视剧和热门综艺节目的在线观看服务。自成立以来，我们始终坚持以用户体验为核心，不断优化播放技术和内容资源，打造流畅、高清、便捷的观影体验。
                </p>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  我们的平台汇集了国内外海量优质影视资源，涵盖<strong>动作片</strong>、<strong>喜剧片</strong>、<strong>爱情片</strong>、<strong>科幻片</strong>、<strong>恐怖片</strong>、<strong>动画片</strong>、<strong>纪录片</strong>、<strong>剧情片</strong>、<strong>悬疑片</strong>、<strong>战争片</strong>、<strong>电视剧</strong>和<strong>综艺节目</strong>等十二大分类频道，满足不同用户的观影需求和喜好。
                </p>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  7777影院采用先进的CDN加速技术和多节点服务器部署方案，确保全国各地的用户都能享受到<strong>在线不缓冲</strong>的流畅播放体验。我们支持1080P和4K超高清画质播放，让每一帧画面都清晰细腻，带给您影院级的视听享受。
                </p>
              </div>
              <div className="flex-shrink-0 w-full lg:w-[400px]">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20cinema%20theater%20interior%20with%20warm%20red%20seats%20elegant%20golden%20lighting%20professional%20architectural%20photography%20clean%20composition%20luxurious%20atmosphere&width=800&height=500&seq=about-cinema&orientation=landscape"
                  alt="7777影院品牌形象"
                  title="7777影院 - 专业在线影视平台"
                  className="w-full h-[280px] object-cover rounded-lg"
                />
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-lg font-heading font-bold text-foreground-900 mb-6">平台优势</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-background-100 rounded-lg p-5">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 mb-3">
                  <i className="ri-flashlight-line text-lg text-primary-600"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-2">极速不缓冲</h3>
                <p className="text-xs text-foreground-600 leading-relaxed">
                  采用CDN加速与智能调度技术，多节点服务器部署，确保全国用户都能享受零缓冲的流畅观影体验。
                </p>
              </div>
              <div className="bg-background-100 rounded-lg p-5">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 mb-3">
                  <i className="ri-hd-line text-lg text-accent-600"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-2">超高清画质</h3>
                <p className="text-xs text-foreground-600 leading-relaxed">
                  全面支持1080P和4K超高清画质，先进的视频编码技术确保画面清晰细腻，还原真实色彩。
                </p>
              </div>
              <div className="bg-background-100 rounded-lg p-5">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 mb-3">
                  <i className="ri-global-line text-lg text-secondary-600"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-2">海量资源</h3>
                <p className="text-xs text-foreground-600 leading-relaxed">
                  涵盖十二大分类频道，收录数万部影视作品，每日持续更新，最新大片和热门剧集一站网罗。
                </p>
              </div>
              <div className="bg-background-100 rounded-lg p-5">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 mb-3">
                  <i className="ri-device-line text-lg text-primary-600"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-2">多端适配</h3>
                <p className="text-xs text-foreground-600 leading-relaxed">
                  全面兼容电脑、平板、手机等各类设备，自适应页面布局，随时随地畅享观影。
                </p>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4">平台声誉</h2>
            <div className="bg-background-100 rounded-lg p-6">
              <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                7777影院自上线以来，凭借出色的播放体验和丰富的影视资源，赢得了广大用户的信赖与好评。我们始终保持对品质的严格要求，致力于为每一位用户提供最好的在线观影服务。
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-6">
                <div className="text-center p-4 bg-background-50 rounded-lg">
                  <p className="text-3xl font-heading font-bold text-primary-500 mb-1">99.7%</p>
                  <p className="text-xs text-foreground-600">播放成功率</p>
                </div>
                <div className="text-center p-4 bg-background-50 rounded-lg">
                  <p className="text-3xl font-heading font-bold text-accent-500 mb-1">50万+</p>
                  <p className="text-xs text-foreground-600">日均活跃用户</p>
                </div>
                <div className="text-center p-4 bg-background-50 rounded-lg">
                  <p className="text-3xl font-heading font-bold text-secondary-500 mb-1">4.8</p>
                  <p className="text-xs text-foreground-600">用户综合评分</p>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-lg font-heading font-bold text-foreground-900 mb-6">联系我们</h2>
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-1">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                      <i className="ri-mail-line text-lg text-primary-600"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h4>
                      <p className="text-sm text-foreground-600">contact@7777yingyuan.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 flex-shrink-0">
                      <i className="ri-customer-service-line text-lg text-accent-600"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-1">在线客服</h4>
                      <p className="text-sm text-foreground-600">7×24小时全天候在线服务</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 flex-shrink-0">
                      <i className="ri-wechat-line text-lg text-secondary-600"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-1">微信公众号</h4>
                      <p className="text-sm text-foreground-600">搜索关注"7777影院"获取最新影视资讯</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex-1">
                <div className="bg-background-100 rounded-lg p-6">
                  <h3 className="text-base font-semibold text-foreground-900 mb-4">给我们留言</h3>
                  <form className="space-y-4" data-readdy-form onSubmit={handleSubmit}>
                    <div>
                      <input
                        type="text"
                        name="name"
                        placeholder="您的姓名"
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        required
                      />
                    </div>
                    <div>
                      <input
                        type="email"
                        name="email"
                        placeholder="您的邮箱"
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        required
                      />
                    </div>
                    <div>
                      <textarea
                        name="message"
                        placeholder="请输入您的留言内容..."
                        rows={4}
                        maxLength={500}
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                        required
                      ></textarea>
                      <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                    </div>
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="honeypot-field"
                    />
                    <div className="flex items-center justify-between">
                      <button
                        type="submit"
                        disabled={formStatus === 'submitting'}
                        className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-background-50 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
                      >
                        {formStatus === 'submitting' ? '发送中...' : '发送留言'}
                      </button>
                    </div>
                    {formStatus === 'success' && (
                      <p className="text-sm text-green-600">留言已成功发送，我们会尽快回复您！</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-red-600">{formError}</p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-lg font-heading font-bold text-foreground-900 mb-6">常见问题</h2>
            <div className="space-y-3">
              {[
                {
                  q: '7777影院的影视资源需要付费吗？',
                  a: '7777影院所有影视资源完全免费在线观看，无需注册会员，无需付费订阅。我们致力于为广大影迷提供最便捷的免费观影体验。',
                },
                {
                  q: '7777影院的画质如何？',
                  a: '7777影院支持1080P和4K超高清画质播放，采用先进的视频编码技术和CDN加速节点，确保您获得流畅、清晰的观影体验。',
                },
                {
                  q: '7777影院的资源更新频率是怎样的？',
                  a: '7777影院资源每日更新，最新上映的电影和热门电视剧会在第一时间同步上线，让您追剧不等待。',
                },
                {
                  q: '如何在7777影院找到想看的影视内容？',
                  a: '您可以通过网站顶部的搜索框直接搜索影片名称，也可以浏览我们精心整理的分类频道，包括动作片、喜剧片、科幻片、电视剧、综艺等十二大分类，方便快捷地找到心仪的影视内容。',
                },
                {
                  q: '7777影院支持哪些设备观看？',
                  a: '7777影院全面支持电脑、平板和手机等各类设备在线观看，页面自适应不同屏幕尺寸，让您随时随地享受观影乐趣。',
                },
              ].map((item, index) => (
                <details key={index} className="bg-background-100 rounded-lg group">
                  <summary className="px-6 py-4 cursor-pointer flex items-center justify-between text-sm font-medium text-foreground-900 hover:text-primary-600 transition-colors">
                    {item.q}
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-6 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </section>
        </div>
      </main>
    </>
  );
}